// FP 간트(자체 구현) — 좌측 데이터 pane (DOM, 렌더 전용)
//
// 좌측을 DOM으로 두는 이유: 텍스트·아이콘·툴팁·다국어가 캔버스보다 훨씬 싸다. 대신 **입력은 받지
// 않는다**(`pointerEvents:none`) — 스크롤/클릭/키보드는 GanttFrame의 스크롤 레이어가 단일 창구로
// 처리하고, 여기서는 좌표에 맞춰 그리기만 한다(입력 경로를 둘로 만들지 않는다).
//
// 보이는 행만 그린다(행 가상화). 컨테이너는 GanttFrame이 스크롤에 맞춰 transform으로 움직인다.
//
// ⚠️ 이 파일의 모든 Box는 `boxSizing: "border-box"`를 명시한다 — 이 앱엔 전역 box-sizing 리셋이
// 없어서(MUI CssBaseline 미사용) 기본값 content-box면 padding·border가 폭/높이에 덧붙어 헤더·캔버스와
// 조용히 어긋나고, 트리 ▶의 히트 판정(GanttFrame.isChevronHit)까지 같이 틀어진다.

import { Fragment } from "react";

import Box from "@mui/material/Box";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

import { UI } from "@plannel/pages/factory-plan/common/gantt/ganttConstants";

// 펼침 표식의 **히트 영역**(정사각). 아이콘 자체는 이보다 작고(16/13px), 이 영역이 클릭 판정과
// 텍스트 시작 위치의 기준이다 — 말단 행도 같은 폭의 빈 자리를 넣어 글자 왼쪽 줄을 맞춘다.
const CHEVRON_W = 22;
// 레벨당 들여쓰기. 14 → **12px**로 압축했다(2026-08-19): 이 트리는 14단계까지 내려가서
// (BOM 전개) 레벨당 2px 차이가 끝에서 28px 차이로 벌어진다.
const INDENT_W = 12;
const CELL_PAD_X = 8;
const CHEVRON_GAP = 4;    // 표식과 글자 사이

// 색·글꼴은 전부 `UI` 토큰(테마 "FP Production", ganttConstants.js)에서 온다 — 여기에 색을 직접 쓰지 말 것.
const CELL_BORDER = `1px solid ${UI.rowLine}`;
const COL_BORDER = `1px solid ${UI.colLine}`;

/**
 * 트리 들여쓰기(px). **padding으로 넣지 않는다** — `box-sizing: border-box`라도 CSS는 padding을
 * 절대 깎지 않으므로, 깊은 레벨에서 `padding-left`가 컬럼 폭보다 커지면 셀의 실제 폭이 padding
 * 합계로 늘어나고 **뒤 컬럼(시작일·종료일)이 행마다 오른쪽으로 밀린다**(컬럼 폭을 줄이면 바로 드러남).
 * 그래서 안쪽 스페이서 요소로 넣고, 컬럼 폭 안에서 ▶와 글자 자리는 남도록 상한을 둔다.
 */
export const treeIndentWidth = (column, row) => {
  const raw = (row.level ?? 0) * INDENT_W;
  const room = column.width - CELL_PAD_X * 2 - CHEVRON_W - CHEVRON_GAP - 24;   // 글자 최소 24px 확보
  return Math.max(0, Math.min(raw, Math.max(0, room)));
};

// 트리 표식 — 계층이 한눈에 읽히도록 3종으로 나눈다(참고: Bryntum / Syncfusion 트리 그리드).
//   level 0(주문 등 최상위, 자식 있음) : 셰브론 16px + 본문색, 22×22 히트 영역에 hover 배경
//   level 1+ (자식이 또 있음)          : 셰브론 13px + muted색, 히트 영역은 같고 hover 배경 없음
//   말단(자식 없음)                    : **아무 글리프도 그리지 않고** 같은 폭의 빈 자리만 둔다
//
// 아이콘은 프로젝트에 이미 있는 MUI `ExpandMore`(chevron-down) 하나만 쓰고 **접힘은 -90° 회전**으로
// 만든다(150ms ease-out). 아이콘 2개를 번갈아 마운트하면 가상 스크롤로 행이 재생성될 때 SVG 노드가
// 매번 교체돼 깜빡일 수 있는데, 회전은 같은 노드의 transform이라 그 위험이 없다.
//
// `aria-hidden` — 이 pane은 렌더 전용 레이어(입력·포커스는 GanttFrame의 스크롤 레이어가 가진다)라
// 아이콘은 순수 장식이다. 상태를 알리는 `aria-expanded`는 role 있는 요소에만 유효한데, 이 pane의
// DOM은 treegrid 구조(행>셀)가 아니라 절대 위치 레이어들의 형제 배치라서 role을 얹을 수 없다
// (병합 셀 레이어가 행의 형제다). ARIA 정식 대응은 별도 과제 — 아래 보고 참고.
const TreeMark = ({ row, expanded, hovered }) => {
  const top = (row.level ?? 0) === 0;

  const frame = {
    width: `${CHEVRON_W}px`, height: `${CHEVRON_W}px`, flex: "0 0 auto", boxSizing: "border-box",
    display: "flex", alignItems: "center", justifyContent: "center"
  };

  // 말단 — 점 글리프를 지우고 자리만 남긴다(글자 시작 위치 정렬).
  if (!row.hasChildren) return <Box component="span" sx={frame} />;

  return (
    <Box
      component="span"
      aria-hidden="true"
      sx={{
        ...frame,
        borderRadius: "4px",
        color: top ? UI.cellFg : UI.cellFgDim,
        // hover 배경은 **최상위 행만**. 좌측 pane은 pointerEvents:none이라 CSS `:hover`가 발동하지
        // 않으므로, 입력을 소유한 GanttFrame이 좌표로 판정해 `hovered`로 내려준다.
        backgroundColor: top && hovered ? UI.rowHover : "transparent",
        transition: "background-color 120ms ease-out"
      }}
    >
      <ExpandMoreIcon
        sx={{
          fontSize: top ? "16px" : "13px",
          transform: expanded ? "rotate(0deg)" : "rotate(-90deg)",
          transition: "transform 150ms ease-out"
        }}
      />
    </Box>
  );
};

const GanttLeftPane = ({
  rows,            // 보이는 행 슬라이스
  columns,         // [{ key, label, width, align, valueGetter, tree, spanRows, cellStyle }]
  spannedKeys,     // Set<colKey> — 병합 컬럼(행 안에서는 자리만 비워둔다)
  spans = [],      // 보이는 병합 셀 [{ key, column, left, top, height, value, row }]
  focusedRowKey,
  focusedColKey,
  selectedRowKey,
  expandedKeys,
  chevronHoverKey   // 펼침 표식 위에 커서가 있는 행 — 입력을 소유한 GanttFrame이 좌표로 판정해 넘긴다
}) => (
  <>
    {rows.map(row => {
      const focused = row.rowKey === focusedRowKey;
      const selected = row.rowKey === selectedRowKey;

      return (
        <Box
          key={row.rowKey}
          sx={{
            position: "absolute",
            left: 0,
            right: 0,
            top: row.top,
            height: row.height,
            boxSizing: "border-box",
            display: "flex",
            alignItems: "center",
            fontSize: UI.cellFontSize,
            color: UI.cellFg,
            backgroundColor: selected ? UI.rowSelected : focused ? UI.rowHover : "transparent"
          }}
        >
          {columns.map(column => {
            // 병합 컬럼은 아래 병합 레이어가 그린다 — 여기서는 폭만 차지하고 **선을 긋지 않는다**
            // (행마다 선을 그으면 합쳐진 셀 안에 가로줄이 지나간다).
            if (spannedKeys?.has(column.key)) {
              return <Box key={column.key} sx={{ width: column.width, flex: "0 0 auto", boxSizing: "border-box", height: "100%" }} />;
            }

            const indent = column.tree ? treeIndentWidth(column, row) : 0;
            const expanded = expandedKeys?.has(row.rowKey);
            const cellFocused = focused && column.key === focusedColKey;
            const emphasize = column.tree && (row.level ?? 0) === 0;

            return (
              <Box
                key={column.key}
                sx={{
                  width: column.width,
                  boxSizing: "border-box",
                  flex: "0 0 auto",
                  height: "100%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: column.align === "center" ? "center" : "flex-start",
                  gap: 0,
                  px: `${CELL_PAD_X}px`,
                  borderRight: COL_BORDER,
                  borderBottom: CELL_BORDER,
                  outline: cellFocused ? `1px solid ${UI.headerAccent}` : "none",
                  outlineOffset: "-1px",
                  overflow: "hidden",
                  whiteSpace: "nowrap",
                  textOverflow: "ellipsis",
                  // 행 단위 조건부 서식(예: 대체자원 강조) — AG Grid `cellStyle`과 같은 역할.
                  ...(column.cellStyle?.(row) ?? null)
                }}
              >
                {column.tree ? (
                  <Fragment>
                    {/* 들여쓰기는 스페이서로 — padding으로 넣으면 셀 폭이 늘어난다(treeIndentWidth 주석) */}
                    <Box component="span" sx={{ width: `${indent}px`, flex: "0 0 auto" }} />
                    {/* 클릭 판정은 GanttFrame이 좌표로 한다(여기는 그림만) */}
                    <TreeMark row={row} expanded={expanded} hovered={row.rowKey === chevronHoverKey} />
                    <Box
                      component="span"
                      sx={{
                        minWidth: 0,      // flex 자식은 기본이 min-content — 없으면 말줄임이 안 걸린다
                        ml: `${CHEVRON_GAP}px`,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        fontWeight: emphasize ? 600 : 400
                      }}
                    >
                      {column.valueGetter(row)}
                    </Box>
                  </Fragment>
                ) : (
                  <Box component="span" sx={{ minWidth: 0, overflow: "hidden", textOverflow: "ellipsis" }}>
                    {column.valueGetter(row)}
                  </Box>
                )}
              </Box>
            );
          })}
        </Box>
      );
    })}

    {/* 병합 셀 레이어 — 같은 값이 연속된 구간을 한 칸으로. 배경은 투명하게 둬 아래 행 강조가 보인다.
        값(라벨)은 셀 **위쪽**에 두고, 병합 구간이 뷰포트 위로 벗어나면 GanttFrame이 스크롤에 맞춰
        translateY로 끌어내려 항상 보이게 한다(old TreeGrid과 같은 sticky 동작). 그래서 라벨을
        별도 span으로 감싼다 — 이 요소를 프레임이 `data-gantt-span-label`로 찾는다. */}
    {spans.map(span => (
      <Box
        key={span.key}
        data-gantt-span=""
        sx={{
          position: "absolute",
          left: span.left,
          top: span.top,
          width: span.column.width,
          height: span.height,
          boxSizing: "border-box",
          display: "flex",
          alignItems: "flex-start",
          justifyContent: span.column.align === "center" ? "center" : "flex-start",
          px: "8px",
          pt: "7px",
          fontSize: UI.cellFontSize,
          // **일반 셀과 같은 글자색**(`cellFg`)이다 — 전에는 `cellFgDim`이라 공장 컬럼만 흐려 보였다
          // (2026-08-21 사용자 지적). 병합 셀은 "덜 중요한 값"이 아니라 **같은 값이 이어지는 구간**을
          // 한 칸으로 묶은 것이라 위계를 낮출 이유가 없다.
          color: UI.cellFg,
          borderRight: COL_BORDER,
          borderBottom: CELL_BORDER,
          overflow: "hidden",
          ...(span.column.cellStyle?.(span.row) ?? null)
        }}
      >
        <Box
          component="span"
          data-gantt-span-label=""
          sx={{
            maxWidth: "100%",
            overflow: "hidden",
            whiteSpace: "nowrap",
            textOverflow: "ellipsis",
            willChange: "transform"
          }}
        >
          {span.value}
        </Box>
      </Box>
    ))}
  </>
);

export const LEFT_PANE_CHEVRON_WIDTH = CHEVRON_W;
export const LEFT_PANE_INDENT_WIDTH = INDENT_W;

export default GanttLeftPane;
